import type { IconIdentifier } from '~ds/lib/icon-types';

/**
 * The icon registry: every glyph the apps and packages may render by name.
 *
 * Icons used dynamically (in functions, watchers, computed properties) must be
 * exported from this file so they can be automatically bundled by @nuxt/icon.
 *
 * Static icon usage in templates is automatically detected by the scan feature.
 */
export const ICONS = <const>{
  add: <IconIdentifier>'hugeicons:add-01',
  activity02: <IconIdentifier>'hugeicons:activity-02',
  alert: <IconIdentifier>'hugeicons:alert-02',
  alertCircle: <IconIdentifier>'hugeicons:alert-circle',
  aiGenerative: <IconIdentifier>'hugeicons:ai-generative',
  analyticsUp: <IconIdentifier>'hugeicons:analytics-up',
  anonymous: <IconIdentifier>'hugeicons:anonymous',
  arrowDown: <IconIdentifier>'hugeicons:arrow-down-01',
  chevronDoubleDown: <IconIdentifier>'hugeicons:arrow-down-double',
  chevronDoubleLeft: <IconIdentifier>'hugeicons:arrow-left-double',
  quiz5: <IconIdentifier>'hugeicons:quiz-05',
  sharing: <IconIdentifier>'hugeicons:sharing',
  teaching: <IconIdentifier>'hugeicons:teaching',
  arrowLeft: <IconIdentifier>'hugeicons:arrow-left-01',
  arrowRight: <IconIdentifier>'hugeicons:arrow-right-01',
  arrowRight2: <IconIdentifier>'hugeicons:arrow-right-02',
  arrowUp: <IconIdentifier>'hugeicons:arrow-up-01',
  arrowLeft2: <IconIdentifier>'hugeicons:arrow-left-02',
  assignments: <IconIdentifier>'hugeicons:assignments',
  audit: <IconIdentifier>'hugeicons:audit-02',
  archive: <IconIdentifier>'hugeicons:archive-03',
  balanceScale: <IconIdentifier>'hugeicons:balance-scale',
  bank: <IconIdentifier>'hugeicons:bank',
  book: <IconIdentifier>'hugeicons:book-02',
  bookOpen: <IconIdentifier>'hugeicons:book-open-02',
  binoculars: <IconIdentifier>'hugeicons:binoculars',
  bookImage: <IconIdentifier>'hugeicons:book-image',
  bookmark: <IconIdentifier>'hugeicons:bookmark-02',
  brain: <IconIdentifier>'hugeicons:brain-01',
  briefcase: <IconIdentifier>'hugeicons:briefcase-01',
  briefcase02: <IconIdentifier>'hugeicons:briefcase-dollar',
  calendar: <IconIdentifier>'hugeicons:calendar-03',
  cancel: <IconIdentifier>'hugeicons:cancel-01',
  champion: <IconIdentifier>'hugeicons:champion',
  chatQuestion: <IconIdentifier>'hugeicons:chat-question',
  checkmarkCircle: <IconIdentifier>'hugeicons:checkmark-circle-02',
  checkmarkBadge: <IconIdentifier>'hugeicons:checkmark-badge-01',
  circleArrowRight: <IconIdentifier>'hugeicons:circle-arrow-right-01',
  coinsDollar: <IconIdentifier>'hugeicons:coins-dollar',
  connect: <IconIdentifier>'hugeicons:connect',
  comment: <IconIdentifier>'hugeicons:comment-01',
  copy: <IconIdentifier>'hugeicons:copy-01',
  computer: <IconIdentifier>'hugeicons:computer',
  cursorClick: <IconIdentifier>'hugeicons:cursor-02',
  dashboardCircle: <IconIdentifier>'hugeicons:dashboard-circle',
  dashboardSquare: <IconIdentifier>'hugeicons:dashboard-square-02',
  clipboardPaste: <IconIdentifier>'hugeicons:clipboard-paste',
  clock: <IconIdentifier>'hugeicons:clock-01',
  delete: <IconIdentifier>'hugeicons:delete-02',
  diploma: <IconIdentifier>'hugeicons:star-award-01',
  directions: <IconIdentifier>'hugeicons:directions-01',
  discoverCircle: <IconIdentifier>'hugeicons:discover-circle',
  discoverSquare: <IconIdentifier>'hugeicons:discover-square',
  dollarCircle: <IconIdentifier>'hugeicons:dollar-circle',
  dollarSquare: <IconIdentifier>'hugeicons:dollar-square',
  download: <IconIdentifier>'hugeicons:download-01',
  downloadCircle: <IconIdentifier>'hugeicons:download-circle-01',
  dragDropVertical: <IconIdentifier>'hugeicons:drag-drop-vertical',
  edit: <IconIdentifier>'hugeicons:pencil-edit-02',
  eye: <IconIdentifier>'hugeicons:eye',
  eyeOff: <IconIdentifier>'hugeicons:view-off-slash',
  favourite: <IconIdentifier>'hugeicons:favourite',
  file: <IconIdentifier>'hugeicons:file-02',
  fileAudio: <IconIdentifier>'hugeicons:file-audio',
  fileEmpty: <IconIdentifier>'hugeicons:file-empty-01',
  fileEmpty02: <IconIdentifier>'hugeicons:file-empty-02',
  fileVideo: <IconIdentifier>'hugeicons:file-video',
  fileView: <IconIdentifier>'hugeicons:file-view',
  filterHorizontal: <IconIdentifier>'hugeicons:filter-horizontal',
  filterMail: <IconIdentifier>'hugeicons:filter-mail',
  flag: <IconIdentifier>'hugeicons:flag-02',
  flash: <IconIdentifier>'hugeicons:flash',
  folder: <IconIdentifier>'hugeicons:folder-01',
  gitCompare: <IconIdentifier>'hugeicons:git-compare',
  gift: <IconIdentifier>'hugeicons:gift',
  globe: <IconIdentifier>'hugeicons:globe-02',
  heading: <IconIdentifier>'hugeicons:heading-02',
  location: <IconIdentifier>'hugeicons:location-05',
  googleSheet: <IconIdentifier>'hugeicons:google-sheet',
  graduateMale: <IconIdentifier>'hugeicons:graduate-male',
  graduationCap: <IconIdentifier>'hugeicons:mortarboard-01',
  headphones: <IconIdentifier>'hugeicons:headphones',
  helpCircle: <IconIdentifier>'hugeicons:help-circle',
  home: <IconIdentifier>'hugeicons:home-03',
  home01: <IconIdentifier>'hugeicons:home-01',
  id: <IconIdentifier>'hugeicons:id',
  idea: <IconIdentifier>'hugeicons:idea-01',
  identityCard: <IconIdentifier>'hugeicons:identity-card',
  image: <IconIdentifier>'hugeicons:image-01',
  information: <IconIdentifier>'hugeicons:information-circle',
  instagram: <IconIdentifier>'hugeicons:instagram',
  jobSearch: <IconIdentifier>'hugeicons:job-search',
  judge: <IconIdentifier>'hugeicons:judge',
  koFi: <IconIdentifier>'hugeicons:ko-fi',
  link: <IconIdentifier>'hugeicons:link-02',
  linkSquare: <IconIdentifier>'hugeicons:link-square-02',
  linkedin01: <IconIdentifier>'hugeicons:linkedin-01',
  linkedin02: <IconIdentifier>'hugeicons:linkedin-02',
  loading: <IconIdentifier>'hugeicons:loading-03',
  lock: <IconIdentifier>'hugeicons:lock',
  lockSolid: <IconIdentifier>'hugeicons-pro-solid:square-lock-02',
  login: <IconIdentifier>'hugeicons:login-02',
  logout: <IconIdentifier>'hugeicons:logout-02',
  logoutCircle: <IconIdentifier>'hugeicons:logout-circle-02',
  mail: <IconIdentifier>'hugeicons:mail-01',
  medalFirstPlace: <IconIdentifier>'hugeicons:medal-first-place',
  meta: <IconIdentifier>'hugeicons:meta',
  minus: <IconIdentifier>'hugeicons:minus-sign',
  moneyBag: <IconIdentifier>'hugeicons:money-bag-02',
  newspaper: <IconIdentifier>'custom:newspaper',
  modernTV: <IconIdentifier>'hugeicons:modern-tv',
  moreHorizontal: <IconIdentifier>'hugeicons:more-horizontal-square-01',
  moreVertical: <IconIdentifier>'hugeicons:more-vertical',
  paypal: <IconIdentifier>'hugeicons:paypal',
  pdf: <IconIdentifier>'hugeicons:pdf-02',
  playCircle: <IconIdentifier>'hugeicons:play-circle-02',
  plus: <IconIdentifier>'hugeicons:plus-sign',
  plusCircle: <IconIdentifier>'hugeicons:plus-sign-circle',
  policeBadge: <IconIdentifier>'hugeicons:police-badge',
  quoteDown: <IconIdentifier>'hugeicons:quote-down',
  reload: <IconIdentifier>'hugeicons:reload',
  repeat: <IconIdentifier>'hugeicons:repeat',
  refresh04: <IconIdentifier>'hugeicons:refresh-04',
  robot: <IconIdentifier>'hugeicons:robotic',
  rocket: <IconIdentifier>'hugeicons:rocket-01',
  school: <IconIdentifier>'hugeicons:school',
  neuralNetwork: <IconIdentifier>'hugeicons:neural-network',
  search: <IconIdentifier>'hugeicons:search-01',
  searching: <IconIdentifier>'hugeicons:searching',
  sent: <IconIdentifier>'hugeicons:sent',
  settings: <IconIdentifier>'hugeicons:settings-01',
  settings02: <IconIdentifier>'hugeicons:settings-02',
  arrowUpRight: <IconIdentifier>'hugeicons:arrow-up-right-01',
  camera: <IconIdentifier>'hugeicons:camera-01',
  fileExport: <IconIdentifier>'hugeicons:file-export',
  gitBranch: <IconIdentifier>'hugeicons:git-branch',
  pin: <IconIdentifier>'hugeicons:pin',
  undo: <IconIdentifier>'hugeicons:undo-02',
  redo: <IconIdentifier>'hugeicons:redo-02',
  userMultiple: <IconIdentifier>'hugeicons:user-multiple',
  video: <IconIdentifier>'hugeicons:video-01',
  share: <IconIdentifier>'hugeicons:share-03',
  shoppingBasket: <IconIdentifier>'hugeicons:shopping-basket-favorite-01',
  smartPhone: <IconIdentifier>'hugeicons:smart-phone-01',
  smile: <IconIdentifier>'hugeicons:smile',
  sortByDown: <IconIdentifier>'hugeicons:sort-by-down-02',
  sparkles: <IconIdentifier>'hugeicons:sparkles',
  sortByUp: <IconIdentifier>'hugeicons:sort-by-up-02',
  stackStar: <IconIdentifier>'hugeicons:stack-star',
  star: <IconIdentifier>'hugeicons:star',
  starAward: <IconIdentifier>'hugeicons:star-award-02',
  students: <IconIdentifier>'hugeicons:students',
  textBold: <IconIdentifier>'hugeicons:text-bold',
  textItalic: <IconIdentifier>'hugeicons:text-italic',
  textStrikethrough: <IconIdentifier>'hugeicons:text-strikethrough',
  textSubscript: <IconIdentifier>'hugeicons:text-subscript',
  textSuperscript: <IconIdentifier>'hugeicons:text-superscript',
  textSearch: <IconIdentifier>'custom:text-search',
  textUnderline: <IconIdentifier>'hugeicons:text-underline',
  listNumber: <IconIdentifier>'hugeicons:left-to-right-list-number',
  listBullet: <IconIdentifier>'hugeicons:left-to-right-list-bullet',
  thumbsDown: <IconIdentifier>'hugeicons:thumbs-down',
  thumbsUp: <IconIdentifier>'hugeicons:thumbs-up',
  tick: <IconIdentifier>'hugeicons:tick-02',
  tiktok: <IconIdentifier>'hugeicons:tiktok',
  timer: <IconIdentifier>'hugeicons:timer-02',
  tvFix: <IconIdentifier>'hugeicons:tv-fix',
  tvIssue: <IconIdentifier>'hugeicons:tv-issue',
  unavailable: <IconIdentifier>'hugeicons:unavailable',
  user: <IconIdentifier>'hugeicons:user',
  userAi: <IconIdentifier>'hugeicons:ai-user',
  userCircle: <IconIdentifier>'hugeicons:user-circle-02',
  userGroup: <IconIdentifier>'hugeicons:user-group',
  userCircleAlt: <IconIdentifier>'hugeicons:user-circle',
  userSettings: <IconIdentifier>'hugeicons:user-settings-02',
  userLove: <IconIdentifier>'hugeicons:user-love-01',
  view: <IconIdentifier>'hugeicons:view',
  waste: <IconIdentifier>'hugeicons:waste',
  wrench: <IconIdentifier>'hugeicons:wrench-01',
  zip: <IconIdentifier>'hugeicons:zip-02',
  stop: <IconIdentifier>'hugeicons:stop',
  steering: <IconIdentifier>'hugeicons:steering',
  mic: <IconIdentifier>'hugeicons:mic-01',
  sentimentPositive: <IconIdentifier>'hugeicons:relieved-01',
  sentimentNeutral: <IconIdentifier>'hugeicons:neutral',
  sentimentNegative: <IconIdentifier>'hugeicons:sad-01',
  play: <IconIdentifier>'hugeicons:play',
  shield02: <IconIdentifier>'hugeicons:shield-02',
  chartHighLow: <IconIdentifier>'hugeicons:chart-high-low',
  chart02: <IconIdentifier>'hugeicons:chart-02',
  film: <IconIdentifier>'hugeicons:film-01',
  note: <IconIdentifier>'hugeicons:note-01',
  pencilEdit: <IconIdentifier>'hugeicons:pencil-edit-01',
  pen: <IconIdentifier>'hugeicons:pen-01',
  addTeam: <IconIdentifier>'hugeicons:add-team',
  code: <IconIdentifier>'hugeicons:code-simple',
  marketingCampaign: <IconIdentifier>'hugeicons:marketing',
  messageQuestion: <IconIdentifier>'hugeicons:message-question',
  smsCode: <IconIdentifier>'hugeicons:sms-code',
  tapScreen: <IconIdentifier>'hugeicons:touch-09',
  userBlock: <IconIdentifier>'hugeicons:user-block-01',
  userWarning: <IconIdentifier>'hugeicons:user-warning-02',
  at: <IconIdentifier>'hugeicons:at',
  cancelCircle: <IconIdentifier>'hugeicons:cancel-circle',
  ellipsis: <IconIdentifier>'hugeicons:more-horizontal',
  layoutLeft: <IconIdentifier>'hugeicons:layout-left',
  lifebuoy: <IconIdentifier>'hugeicons:lifebuoy',
  magicWand: <IconIdentifier>'hugeicons:magic-wand-01',
  package: <IconIdentifier>'hugeicons:package',
  panelLeftClose: <IconIdentifier>'hugeicons:panel-left-close',
  panelLeftOpen: <IconIdentifier>'hugeicons:panel-left-open',
  panelRightClose: <IconIdentifier>'hugeicons:panel-right-close',
  panelRightOpen: <IconIdentifier>'hugeicons:panel-right-open',
  serverStack: <IconIdentifier>'hugeicons:server-stack-01',
  circle: <IconIdentifier>'hugeicons:circle',
  key: <IconIdentifier>'hugeicons:key-01',
  route: <IconIdentifier>'hugeicons:route-01',
  target: <IconIdentifier>'hugeicons:target-01',
  zoomIn: <IconIdentifier>'hugeicons:zoom-in-area',
  zoomOut: <IconIdentifier>'hugeicons:zoom-out-area',

  // Pro (generated solid-rounded collection — refresh with `pnpm icons:sync`)
  alertCircleSuccess: <IconIdentifier>'hugeicons-pro-solid:checkmark-circle-02',
  alertCircleError: <IconIdentifier>'hugeicons-pro-solid:cancel-circle',
  alertCircleInfo: <IconIdentifier>'hugeicons-pro-solid:information-circle',
  alertCircleWarning: <IconIdentifier>'hugeicons-pro-solid:alert-circle',
  arrowRightSolid: <IconIdentifier>'hugeicons-pro-solid:arrow-right-02',
  arrowUpSolid: <IconIdentifier>'hugeicons-pro-solid:arrow-up-02',
  arrowUpRightSolid: <IconIdentifier>'hugeicons-pro-solid:arrow-up-right-01',
  cardsSolid: <IconIdentifier>'hugeicons-pro-solid:cards',
  favouriteSolid: <IconIdentifier>'hugeicons-pro-solid:favourite',
  ideaSolid: <IconIdentifier>'hugeicons-pro-solid:idea-01',
  jobSearchSolid: <IconIdentifier>'hugeicons-pro-solid:job-search',
  linkedinSolid: <IconIdentifier>'hugeicons-pro-solid:linkedin-01',
  listSolid: <IconIdentifier>'hugeicons-pro-solid:check-list',
  menuSolid: <IconIdentifier>'hugeicons-pro-solid:menu-01',
  navigationSolid: <IconIdentifier>'hugeicons-pro-solid:navigation-05',
  rocketSolid: <IconIdentifier>'hugeicons-pro-solid:rocket-01',
  schoolSolid: <IconIdentifier>'hugeicons-pro-solid:school-01',
  stackStarSolid: <IconIdentifier>'hugeicons-pro-solid:stack-star',
  starSolid: <IconIdentifier>'hugeicons-pro-solid:star',
  starAwardSolid: <IconIdentifier>'hugeicons-pro-solid:star-award-01',
  tickSolid: <IconIdentifier>'hugeicons-pro-solid:tick-02',
  viewSolid: <IconIdentifier>'hugeicons-pro-solid:view',

  // Custom (bespoke SVGs in assets/icons/custom — 24-grid rules enforced by icons.spec.ts)
  age18Circle: <IconIdentifier>'custom:age-18-circle',
  arrowRightBold: <IconIdentifier>'custom:arrow-right-bold',
  brandClever: <IconIdentifier>'custom:brand-clever',
  brandGoogle: <IconIdentifier>'custom:brand-google',
  brandMicrosoft: <IconIdentifier>'custom:brand-microsoft',
  jobSearchFavouriteSolid: <IconIdentifier>'custom:job-search-favourite-solid',
  userAlert: <IconIdentifier>'custom:user-alert',
};

/**
 * Extract all icon values as an array for use in nuxt.config.ts
 * This allows automatic bundling of all icons defined here.
 */
export const ICON_VALUES = Object.values(ICONS);

/**
 * Type representing all valid icon identifiers from the ICONS constant.
 *
 * This is a strict subset of `IconIdentifier` that only includes icons defined in this file.
 * Use this type for component props to ensure:
 * - Icons are automatically bundled by @nuxt/icon
 * - Better autocomplete and type safety
 * - Icons are centralized and discoverable
 *
 * If you need to use an icon that exists in hugeicons but isn't in ICONS, add it to this file first.
 *
 * For cases where you need to accept any valid icon identifier (e.g., from external sources),
 * use `IconIdentifier` from `~ds/lib/icon-types` instead.
 *
 * @example
 * ```ts
 * // ✅ Good - ensures icon is bundled
 * interface Props {
 *   icon: IconName;
 * }
 *
 * // ⚠️ Use only when accepting icons from external sources
 * interface Props {
 *   icon: IconIdentifier;
 * }
 * ```
 */
export type IconName = (typeof ICONS)[keyof typeof ICONS];
